<template>
  <div
    class="task-item group flex items-start gap-[6px] px-[4px] py-[2px] rounded hover:bg-gray-50 dark:hover:bg-gray-100"
    :class="{
      'opacity-50': task.completed_at && !organizeMode,
      'bg-teal-50 dark:bg-teal-900/20 ring-1 ring-inset ring-teal-200/80 dark:ring-teal-700/50': selected,
    }"
    @click="onRowClick"
  >
    <!-- Drag handle: same first-line box as checkbox -->
    <span
      class="task-item__lead drag-handle flex w-[14px] flex-shrink-0 cursor-grab items-center justify-center text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 hover:text-gray-500 active:cursor-grabbing"
      title="Drag to reorder"
      @click.stop
    >
      <svg class="h-[12px] w-[12px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="9" cy="7" r="1.6"/>
        <circle cx="15" cy="7" r="1.6"/>
        <circle cx="9" cy="12" r="1.6"/>
        <circle cx="15" cy="12" r="1.6"/>
        <circle cx="9" cy="17" r="1.6"/>
        <circle cx="15" cy="17" r="1.6"/>
      </svg>
    </span>

    <!-- Complete (normal) or multi-select (organize) — centered in first text line -->
    <span class="task-item__lead flex w-[16px] flex-shrink-0 items-center justify-center">
      <input
        v-if="organizeMode"
        type="checkbox"
        :checked="selected"
        @click.stop="onSelectClick"
        class="h-[14px] w-[14px] cursor-pointer rounded border-gray-300 accent-teal-500 focus:ring-0 focus:ring-offset-0"
        title="Select for bulk edit"
      />
      <input
        v-else
        type="checkbox"
        :checked="!!task.completed_at"
        @click.stop="$emit('toggle-complete')"
        class="h-[14px] w-[14px] cursor-pointer rounded border-gray-300 accent-blue-500 focus:ring-0 focus:ring-offset-0"
      />
    </span>

    <!-- Title -->
    <div class="task-item__title min-w-0 flex-1" @click.stop="startEditing">
      <textarea
        v-if="editing"
        ref="titleInput"
        v-model="editTitle"
        @blur="saveTitle"
        @keydown.enter.prevent="saveTitle"
        @keydown.escape="cancelEdit"
        @input="autoResize"
        rows="1"
        class="task-item__title-text m-0 w-full resize-none rounded border-none bg-yellow-50 p-0 focus:outline-none focus:ring-0 dark:bg-gray-200"
      ></textarea>
      <span
        v-else
        class="task-item__title-text cursor-pointer break-words"
        :class="{ 'line-through': task.completed_at && !organizeMode }"
      >
        {{ task.title }}
      </span>
    </div>

    <!-- Right meta -->
    <div class="task-item__meta flex flex-shrink-0 items-start gap-[2px]">
      <span class="task-item__lead flex w-[10px] items-center justify-center">
        <span
          v-if="isRecent && !editing"
          class="h-[5px] w-[5px] rounded-full bg-blue-400"
          title="Recently updated"
        ></span>
      </span>

      <!-- Organize mode: one-click section chips -->
      <template v-if="organizeMode">
        <div class="task-item__lead flex items-center gap-[2px]">
          <button
            v-for="s in sections"
            :key="s.id"
            @click.stop="quickMoveSection(s.id)"
            class="section-chip flex h-[16px] min-w-[18px] items-center justify-center rounded px-[3px] text-[10px] font-semibold uppercase leading-none transition-colors"
            :class="s.id === task.section_id
              ? 'bg-teal-500 text-white'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:bg-gray-200 dark:text-gray-500 dark:hover:bg-gray-300'"
            :title="`Move to ${s.name}`"
          >
            {{ sectionAbbrev(s) }}
          </button>
        </div>
      </template>

      <!-- Normal mode: detail + section dropdown -->
      <template v-else>
        <button
          @click.stop="$emit('select')"
          class="task-item__lead task-item__action flex w-[20px] items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-200 dark:hover:text-gray-700"
          title="Details"
        >
          <svg class="h-[14px] w-[14px]" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
        </button>

        <div class="relative" ref="sectionDropdownRef">
          <button
            @click.stop="showSectionDropdown = !showSectionDropdown"
            class="task-item__lead task-item__action flex w-[20px] items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-200 dark:hover:text-gray-700"
            title="Move to section"
          >
            <svg class="h-[14px] w-[14px]" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 7h10"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 12h7"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 17h7"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 14l3 3 3-3"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M18 17V9"/>
            </svg>
          </button>
          <div
            v-if="showSectionDropdown"
            class="absolute right-0 top-full z-40 mt-[2px] w-[148px] rounded-md border border-gray-200 bg-white py-[2px] shadow-lg dark:border-gray-300 dark:bg-gray-50"
          >
            <button
              v-for="s in sections"
              :key="s.id"
              @click.stop="changeSection(s.id)"
              class="w-full px-[10px] py-[4px] text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-200"
              :class="{ 'bg-teal-50 font-medium': s.id === task.section_id }"
            >
              {{ s.name }}
            </button>
          </div>
        </div>
      </template>

      <!-- Project: muted secondary — same face/size family as title, never louder than title -->
      <div class="relative w-[112px]" ref="projectDropdownRef">
        <button
          @click.stop="showProjectDropdown = !showProjectDropdown"
          class="task-item__lead flex w-full items-center justify-end gap-[4px] rounded px-[2px] text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-200"
          title="Project"
        >
          <template v-if="task.project_name">
            <span
              v-if="projectColor"
              class="h-[6px] w-[6px] flex-shrink-0 rounded-full opacity-70"
              :style="{ background: projectColor }"
            ></span>
            <span class="task-item__project truncate">{{ task.project_name }}</span>
          </template>
          <svg
            v-else
            class="h-[14px] w-[14px] text-gray-400"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
          </svg>
        </button>
        <div
          v-if="showProjectDropdown"
          class="absolute right-0 top-full z-40 mt-[2px] max-h-[240px] w-[176px] overflow-y-auto rounded-md border border-gray-200 bg-white py-[2px] shadow-lg dark:border-gray-300 dark:bg-gray-50"
        >
          <button
            @click.stop="changeProject(null)"
            class="w-full px-[10px] py-[4px] text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-200"
            :class="{ 'bg-teal-50 font-medium': !task.project_id }"
          >
            No project
          </button>
          <button
            v-for="p in sortedProjects"
            :key="p.id"
            @click.stop="changeProject(p.id)"
            class="flex w-full items-center gap-[6px] px-[10px] py-[4px] text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-200"
            :class="{ 'bg-teal-50 font-medium': p.id === task.project_id }"
          >
            <span
              v-if="p.color"
              class="h-[6px] w-[6px] flex-shrink-0 rounded-full"
              :style="{ background: p.color }"
            ></span>
            {{ p.display_label || p.name }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  task: Object,
  projects: Array,
  sections: Array,
  selected: { type: Boolean, default: false },
  organizeMode: { type: Boolean, default: false },
})
const emit = defineEmits([
  'select', 'toggle-complete', 'update', 'delete',
  'move-section', 'change-project', 'toggle-select', 'range-select',
])

const editing = ref(false)
const editTitle = ref('')
const titleInput = ref(null)
const showSectionDropdown = ref(false)
const showProjectDropdown = ref(false)
const sectionDropdownRef = ref(null)
const projectDropdownRef = ref(null)

const isRecent = computed(() => {
  if (!props.task.updated_at) return false
  return (Date.now() - new Date(props.task.updated_at).getTime()) < 3600000
})
const projectColor = computed(() => props.projects?.find(p => p.id === props.task.project_id)?.color || null)
const sortedProjects = computed(() =>
  [...(props.projects || [])].sort((a, b) =>
    (a.display_label || a.name).localeCompare(b.display_label || b.name)
  )
)

const SLUG_ABBREV = {
  masterlist: 'M',
  today: 'T',
  next: 'N',
  someday: 'S',
  archive: 'A',
}

function sectionAbbrev(s) {
  if (s.slug && SLUG_ABBREV[s.slug]) return SLUG_ABBREV[s.slug]
  const name = (s.name || '?').trim()
  return name.charAt(0).toUpperCase()
}

function onSelectClick(e) {
  if (e.shiftKey) {
    emit('range-select', props.task.id)
    return
  }
  emit('toggle-select', props.task.id)
}

function onRowClick(e) {
  if (!props.organizeMode) return
  // In organize mode, row click (outside checkbox/chips) toggles selection
  if (e.shiftKey) {
    emit('range-select', props.task.id)
    return
  }
  emit('toggle-select', props.task.id)
}

function startEditing() {
  if (props.organizeMode) {
    emit('toggle-select', props.task.id)
    return
  }
  editing.value = true
  editTitle.value = props.task.title
  nextTick(() => { if (titleInput.value) { titleInput.value.focus(); autoResize() } })
}
function autoResize() {
  if (titleInput.value) {
    titleInput.value.style.height = 'auto'
    titleInput.value.style.height = titleInput.value.scrollHeight + 'px'
  }
}
function saveTitle() {
  const trimmed = editTitle.value.trim()
  if (!trimmed) {
    emit('delete', props.task.id)
  } else if (trimmed !== props.task.title) {
    emit('update', props.task.id, { title: trimmed })
  }
  editing.value = false
}
function cancelEdit() { editing.value = false }
function changeSection(sectionId) {
  if (sectionId !== props.task.section_id) emit('move-section', props.task.id, sectionId)
  showSectionDropdown.value = false
}
function quickMoveSection(sectionId) {
  if (sectionId !== props.task.section_id) emit('move-section', props.task.id, sectionId)
}
function changeProject(projectId) {
  if (projectId !== props.task.project_id) emit('change-project', props.task.id, projectId)
  showProjectDropdown.value = false
}
function handleClickOutside(e) {
  if (sectionDropdownRef.value && !sectionDropdownRef.value.contains(e.target)) showSectionDropdown.value = false
  if (projectDropdownRef.value && !projectDropdownRef.value.contains(e.target)) showProjectDropdown.value = false
}
onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>

<style scoped>
/*
  Obsidian-like task row type:
  - Inter (via --font-sans)
  - title is the hero (400, softer body color)
  - project/meta quieter (same weight, muted color)
  - looser line-height so the list reads as document text, not UI chrome
*/
.task-item {
  --task-line: 22px; /* 15px × ~1.47 */
  font-family: var(--font-sans), "Inter", ui-sans-serif, system-ui, sans-serif;
}
.task-item__lead {
  height: var(--task-line);
  min-height: var(--task-line);
}
.task-item__title {
  line-height: var(--task-line);
}
.task-item__title-text {
  font-size: 15px;
  font-weight: 400;
  line-height: var(--task-line);
  color: #374151; /* gray-700 — soft body, not pure black */
}
.task-item__title-text.line-through {
  color: #9ca3af; /* gray-400 when completed */
}
html.dark .task-item__title-text {
  color: #cdcdce; /* gray-800 token inverted in dark */
}
html.dark .task-item__title-text.line-through {
  color: #6b6b6c;
}
.task-item__project {
  font-size: 13px;
  font-weight: 400;
  line-height: var(--task-line);
  color: #6b7280; /* gray-500 — secondary to title */
}
html.dark .task-item__project {
  color: #9e9e9f;
}
</style>
